import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../../main-css/news-css/NewsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_PROD_URL;

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const bottomObserverRef = useRef(null);
  const fetchingRef = useRef(false);

  const fetchArticles = useCallback(async () => {
    if (fetchingRef.current || loading || !hasMore) return;

    fetchingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/news/rss?after=${offset}`,);
      const data = await response.json();

      if (data.articles?.length > 0) {
        // Use a function to update articles to ensure we have the latest state
        setArticles(prevArticles => {
          // Create a Map of existing articles using their links as keys
          const existingArticles = new Map(
            prevArticles.map(article => [article.link?.[0], article])
          );    
          // Filter out any duplicates from the new articles
          const newArticles = data.articles.filter(
            article => !existingArticles.has(article.link?.[0])
          );          
          return [...prevArticles, ...newArticles];
        });

        setOffset(data.nextAfter);
        setHasMore(data.nextAfter !== null);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error fetching the RSS feed:', error);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [offset, loading, hasMore]);

  // Initial load
  useEffect(() => {
    fetchArticles();
  }, []);

 // Intersection Observer setup
 useEffect(() => {
  const observer = new IntersectionObserver(
    entries => {
      const firstEntry = entries[0];
      console.log('Intersection observed:', firstEntry.isIntersecting); // Debug log
      
      if (firstEntry.isIntersecting && !loading && hasMore) {
        console.log('Triggering fetch with offset:', offset); // Debug log
        fetchArticles();
      }
    },
    { 
      root: null, 
      threshold: 0.1, // Reduced threshold - triggers earlier
      rootMargin: '200px 0px' // Load more content 200px before reaching the bottom
    }
  );

  const currentRef = bottomObserverRef.current;
  if (currentRef) {
    observer.observe(currentRef);
    console.log('Observer attached to:', currentRef); // Debug log
  }

  return () => {
    if (currentRef) {
      observer.unobserve(currentRef);
    }
  };
}, [fetchArticles, loading, hasMore, offset]);

  const getArticleKey = (article, index) => {
    return `${article.link?.[0]}-${article.pubDate?.[0]}-${index}`;
  };

  return (
    <div className="news-page">
      <div className="news-page-container">
        <h2 className="news-page-title">News</h2>
        <div className="news-page-articles">
          {articles.map((article, index) => (
            <div key={getArticleKey(article, index)} className="news-page-tile">
              {(article['media:thumbnail']?.[0]?.url || article.enclosure?.[0]?.url || article['media:content']?.[0]?.url) ? (
                <img
                  src={article['media:thumbnail']?.[0]?.url || article.enclosure?.[0]?.url || article['media:content']?.[0]?.url}
                  alt="Article Thumbnail"
                  className="news-page-thumbnail"
                  onError={(e) => (e.target.src = '/Images/AnimeBibleLogoTP.png')}
                />
              ) : (
                <img
                  src="/Images/AnimeBibleLogoTP.png"
                  alt="Placeholder Thumbnail"
                  className="news-page-thumbnail"
                />
              )}
              <div className="news-page-text">
                <a
                  href={article.link?.[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="news-page-link"
                >
                  {article.title?.[0] || 'Untitled'}
                </a>
                <p className="news-page-description">
                  {article.description?.[0] || 'No description available'}
                </p>
                <p className="news-page-date">
                  {new Date(article.pubDate?.[0]).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
  
          {/* Infinite Scroll Observer */}
          <div
            ref={bottomObserverRef}
            className="observer-element"
            style={{ height: '20px', margin: '20px 0' }}
          >
            {loading && <div className="loading-indicator">Loading more articles...</div>}
          </div>
        </div>
  
        {!hasMore && articles.length > 0 && (
          <p className="no-more-text">No more articles available</p>
        )}
      </div>
    </div>
  );
  

};

export default NewsPage;